import { PLATFORM_SKIES, PLATFORM_SOUNDSCAPES, PLATFORM_TEXTURES } from './assets.generated.js';
import { RESERVED_WORLD_SLUGS, WORLD_FILE, WORLD_FORMAT_VERSIONS, WORLD_LICENCES, WORLD_PACKAGE_LIMITS as L, WORLD_SLUG, fileKindOf, } from './format.js';
import { BLOCK_SETTINGS, STATEFUL, STATEFUL_PARTICLES, checkValue, readBlock, } from './settings.js';
import { sentencesOf } from './sentences.js';
import { UNSAFE_TEXT, isRecord, lookup, problem, show } from './util.js';
import { V2_OPTIONAL_KEYS, V2_ROOT_KEYS, blockRules, extrasProblems, lightRules } from './v2.js';
const FEATURE = /^[a-z][a-z0-9-]{0,23}$/;
/** Keys any format may have: old feature names (the maker's), the order (the platform's; order.ts). */
const ANY_VERSION_OPTIONAL_KEYS = ['eerdereOnderdelen', 'order'];
const FEATURE_KEYS = ['name', 'initial', 'seconds', 'round', 'states'];
/** The package's own settings (not a block's), with the same kinds of spec. */
const TOP = {
    sky: {
        image: { kind: 'material', about: '' },
        seen: { kind: 'boolean', about: '' },
        exposure: { kind: 'number', min: 0.1, max: 4, about: '' },
        environment: { kind: 'number', min: 0, max: 4, about: '' },
        yaw: { kind: 'number', min: -180, max: 180, about: '' },
        drift: { kind: 'number', min: -10, max: 10, about: '' },
    },
    sun: {
        intensity: { kind: 'number', min: 0, max: 10, about: '' },
        colour: { kind: 'colour', about: '' },
        from: 'object',
    },
};
const ROOT_KEYS = [
    'formatVersion',
    'slug',
    'name',
    'description',
    'sky',
    'sun',
    'fog',
    'glow',
    'background',
    'terrain',
    'blocks',
    'features',
    'sound',
    'credits',
];
function text(value, path, max, problems) {
    if (typeof value !== 'string' || value.trim().length < 1 || value.length > max) {
        problems.push(problem(path, `moet een tekst van 1 tot ${max} tekens zijn`));
    }
    else if (UNSAFE_TEXT.test(value)) {
        problems.push(problem(path, 'mag geen stuurtekens of tekstrichting-tekens bevatten'));
    }
}
function keys(value, wanted, path, problems, optional = []) {
    for (const name of wanted)
        if (value[name] === undefined)
            problems.push(problem(`${path}/${name}`, 'ontbreekt'));
    for (const name of Object.keys(value))
        if (!wanted.includes(name) && !optional.includes(name))
            problems.push(problem(`${path}/${show(name)}`, 'onbekend veld'));
}
function settings(value, specs, path, problems) {
    if (!isRecord(value)) {
        problems.push(problem(path, 'moet een object zijn'));
        return;
    }
    keys(value, Object.keys(specs), path, problems);
    for (const [name, spec] of Object.entries(specs)) {
        if (spec !== 'object' && value[name] !== undefined)
            problems.push(...checkValue(spec, value[name], `${path}/${name}`));
    }
}
/**
 * The package's format (W3): every key, every setting in range, every file it names present and of the
 * right kind, every file it gets named and credited, platform names that exist, features whose states
 * change only stateful settings of blocks it has. An unknown format version or block type refuses it whole.
 */
export function checkWorldFormat(json, files) {
    try {
        const result = format(json, files);
        return { ...result, problems: capped(result.problems) };
    }
    catch {
        // Total: whatever the input, the answer is a list of problems, never a throw.
        return { problems: [problem('', 'wereld.json heeft een vorm die niet te lezen is')] };
    }
}
const MAX_PROBLEMS = 100;
function capped(problems) {
    if (problems.length <= MAX_PROBLEMS)
        return problems;
    return [
        ...problems.slice(0, MAX_PROBLEMS),
        problem('', `en nog ${problems.length - MAX_PROBLEMS} andere problemen`),
    ];
}
function format(json, files) {
    const problems = [];
    if (!isRecord(json))
        return { problems: [problem('', 'wereld.json moet een object zijn')] };
    const fileList = filesOf(files, problems);
    const formatVersion = json['formatVersion'];
    if (!WORLD_FORMAT_VERSIONS.includes(formatVersion)) {
        return {
            problems: [
                problem('/formatVersion', `onbekende formaatversie ${show(formatVersion)} (deze headset kent: ${WORLD_FORMAT_VERSIONS.join(', ')})`),
            ],
        };
    }
    const version = formatVersion;
    const rootKeys = [...ROOT_KEYS, ...(version === 2 ? V2_ROOT_KEYS : [])];
    if (typeof json['slug'] !== 'string' || !WORLD_SLUG.test(json['slug'])) {
        problems.push(problem('/slug', 'moet een naam zijn als "strand" of "zee-2" (2–40 tekens: kleine letters, cijfers, -)'));
    }
    if (typeof json['slug'] === 'string' &&
        RESERVED_WORLD_SLUGS.includes(json['slug']))
        problems.push(problem('/slug', `"${json['slug']}" is de naam van een plek die geen wereld is`));
    for (const name of rootKeys)
        if (json[name] === undefined)
            problems.push(problem(`/${name}`, 'ontbreekt'));
    const optional = [
        ...ANY_VERSION_OPTIONAL_KEYS,
        ...(version === 2 ? V2_OPTIONAL_KEYS : []),
    ];
    for (const name of Object.keys(json)) {
        if (!rootKeys.includes(name) && !optional.includes(name))
            problems.push(problem(`/${show(name)}`, 'onbekend veld'));
    }
    const named = isRecord(json['name']) ? json['name'] : {};
    if (json['name'] !== undefined) {
        if (!isRecord(json['name']))
            problems.push(problem('/name', 'moet {nl, en} zijn'));
        else {
            keys(named, ['nl', 'en'], '/name', problems);
            if (named['nl'] !== undefined)
                text(named['nl'], '/name/nl', L.name, problems);
            if (named['en'] !== undefined)
                text(named['en'], '/name/en', L.nameEn, problems);
        }
    }
    if (json['description'] !== undefined)
        text(json['description'], '/description', L.description, problems);
    if (json['sky'] !== undefined)
        settings(json['sky'], TOP['sky'], '/sky', problems);
    if (json['sun'] !== undefined) {
        settings(json['sun'], TOP['sun'], '/sun', problems);
        const from = isRecord(json['sun']) ? json['sun']['from'] : null;
        if (from !== null && from !== undefined) {
            settings(from, {
                azimuth: { kind: 'number', min: -180, max: 180, about: '' },
                elevation: { kind: 'number', min: 1, max: 90, about: '' },
            }, '/sun/from', problems);
        }
    }
    if (json['fog'] !== undefined && json['fog'] !== null) {
        settings(json['fog'], { density: { kind: 'number', min: 0, max: 0.05, about: '' }, colour: 'object' }, '/fog', problems);
        const colour = isRecord(json['fog']) ? json['fog']['colour'] : null;
        if (colour !== null && colour !== undefined)
            problems.push(...checkValue({ kind: 'colour', about: '' }, colour, '/fog/colour'));
    }
    if (json['glow'] !== undefined)
        problems.push(...checkValue({ kind: 'number', min: 0, max: 5, about: '' }, json['glow'], '/glow'));
    if (json['background'] !== undefined && json['background'] !== null) {
        problems.push(...checkValue({ kind: 'colour', about: '' }, json['background'], '/background'));
    }
    const seen = isRecord(json['sky']) ? json['sky']['seen'] : undefined;
    if (seen === true && json['background'] !== null && json['background'] !== undefined) {
        problems.push(problem('/background', 'moet null zijn als de lucht te zien is (sky.seen)'));
    }
    else if (seen === false && json['background'] === null) {
        problems.push(problem('/background', 'moet een kleur zijn als de lucht niet te zien is (sky.seen: false)'));
    }
    // Blocks.
    const blocks = [];
    const raw = Array.isArray(json['blocks']) ? json['blocks'] : [];
    if (json['blocks'] !== undefined && !Array.isArray(json['blocks']))
        problems.push(problem('/blocks', 'moet een lijst zijn'));
    if (raw.length > L.blocks)
        problems.push(problem('/blocks', `hooguit ${L.blocks} blokken`));
    const ids = new Set();
    const rawIndex = new Map();
    // Past the limit nothing is walked: the refusal above says it all.
    raw.slice(0, L.blocks).forEach((entry, i) => {
        const read = readBlock(entry, `/blocks/${i}`, version);
        problems.push(...read.problems);
        const id = isRecord(entry) ? entry['id'] : undefined;
        if (typeof id === 'string') {
            if (ids.has(id))
                problems.push(problem(`/blocks/${i}/id`, `er is al een blok "${show(id)}"`));
            ids.add(id);
        }
        if (read.block) {
            blocks.push(read.block);
            rawIndex.set(read.block, i);
        }
    });
    // Files: what the JSON names, and of which kind.
    const uses = [];
    if (version === 2) {
        problems.push(...extrasProblems(json, uses));
        problems.push(...blockRules(blocks, json['staged'], (b) => rawIndex.get(b) ?? 0, isRecord(json['features']) ? Object.keys(json['features']).length : 0));
    }
    const sky = isRecord(json['sky']) && isRecord(json['sky']['image']) ? json['sky']['image'] : {};
    if (sky['file'] !== undefined)
        uses.push({ path: '/sky/image', name: sky['file'], kind: 'sky' });
    else if (sky['platform'] !== undefined && !lookup(PLATFORM_SKIES, sky['platform'])) {
        problems.push(problem('/sky/image', `het platform heeft geen lucht "${show(sky['platform'])}"`));
    }
    const terrain = isRecord(json['terrain']) ? json['terrain'] : undefined;
    if (json['terrain'] !== undefined) {
        if (!terrain)
            problems.push(problem('/terrain', 'moet {glb, heights} zijn'));
        else {
            keys(terrain, ['glb', 'heights'], '/terrain', problems);
            const glbs = Array.isArray(terrain['glb']) ? terrain['glb'] : [];
            if (!Array.isArray(terrain['glb']) || glbs.length > L.glbs) {
                problems.push(problem('/terrain/glb', `moet een lijst van hooguit ${L.glbs} GLB's zijn`));
            }
            glbs.forEach((name, i) => {
                if (typeof name === 'string' && glbs.indexOf(name) !== i) {
                    problems.push(problem(`/terrain/glb/${i}`, `"${show(name)}" staat al in de lijst`));
                }
                uses.push({ path: `/terrain/glb/${i}`, name, kind: 'glb' });
            });
            const heights = terrain['heights'];
            if (!isRecord(heights))
                problems.push(problem('/terrain/heights', 'moet {file, x, z, columns, rows} zijn'));
            else {
                keys(heights, ['file', 'x', 'z', 'columns', 'rows'], '/terrain/heights', problems);
                uses.push({ path: '/terrain/heights/file', name: heights['file'], kind: 'heights' });
                for (const axis of ['x', 'z']) {
                    const span = heights[axis];
                    const ok = Array.isArray(span) &&
                        span.length === 2 &&
                        span.every((v) => typeof v === 'number' && Number.isFinite(v) && Math.abs(v) <= L.extent) &&
                        span[0] < span[1];
                    if (!ok)
                        problems.push(problem(`/terrain/heights/${axis}`, `moet [van, tot] zijn met van < tot, binnen ±${L.extent} m`));
                }
                for (const side of ['columns', 'rows']) {
                    problems.push(...checkValue({
                        kind: 'number',
                        min: L.heightsSide.min,
                        max: L.heightsSide.max,
                        integer: true,
                        about: '',
                    }, heights[side], `/terrain/heights/${side}`));
                }
                const file = fileList?.find((f) => f.name === heights['file']);
                const expected = Number(heights['columns']) * Number(heights['rows']) * 4;
                if (file && Number.isFinite(expected) && file.bytes !== expected) {
                    problems.push(problem('/terrain/heights/file', `is ${file.bytes} bytes; ${show(heights['columns'])} × ${show(heights['rows'])} hoogtes zijn ${expected} bytes`));
                }
            }
        }
    }
    blocks.forEach((block, i) => {
        if (block.type !== 'surface')
            return;
        if ('file' in block.texture) {
            uses.push({ path: `/blocks/${i}/texture`, name: block.texture.file, kind: 'texture' });
            if (block.maps.length !== 1 || block.maps[0] !== 'diff') {
                problems.push(problem(`/blocks/${i}/maps`, 'een eigen textuur heeft alleen ["diff"]'));
            }
        }
        else {
            const set = lookup(PLATFORM_TEXTURES, block.texture.platform);
            if (!set)
                problems.push(problem(`/blocks/${i}/texture`, `het platform heeft geen textuur "${show(block.texture.platform)}"`));
            else
                for (const map of block.maps)
                    if (!set[map])
                        problems.push(problem(`/blocks/${i}/maps`, `"${show(block.texture.platform)}" heeft geen ${map}`));
        }
        if (block.overlay && !lookup(PLATFORM_TEXTURES, block.overlay.platform)?.diff) {
            problems.push(problem(`/blocks/${i}/overlay/platform`, `het platform heeft geen textuur "${show(block.overlay.platform)}"`));
        }
    });
    const sound = isRecord(json['sound']) ? json['sound'] : undefined;
    if (json['sound'] !== undefined) {
        if (!sound)
            problems.push(problem('/sound', 'moet {soundscape, volume} of {file, volume} zijn'));
        else {
            const which = Object.hasOwn(sound, 'file') ? 'file' : 'soundscape';
            keys(sound, [which, 'volume'], '/sound', problems);
            problems.push(...checkValue({ kind: 'number', min: -30, max: 6, about: '' }, sound['volume'], '/sound/volume'));
            if (which === 'file')
                uses.push({ path: '/sound/file', name: sound['file'], kind: 'sound' });
            else if (!lookup(PLATFORM_SOUNDSCAPES, sound['soundscape'])) {
                problems.push(problem('/sound/soundscape', `het platform heeft geen geluid "${show(sound['soundscape'])}"`));
            }
        }
    }
    const present = new Map((fileList ?? []).map((f) => [f.name, f]));
    const used = new Set();
    for (const use of uses) {
        if (typeof use.name !== 'string' || !WORLD_FILE.test(use.name)) {
            problems.push(problem(use.path, 'moet een bestandsnaam zijn als "land.glb" (kleine letters, geen mappen)'));
            continue;
        }
        used.add(use.name);
        if (fileKindOf(use.name) !== use.kind)
            problems.push(problem(use.path, `"${show(use.name)}" is geen ${use.kind}-bestand`));
        if (fileList && !present.has(use.name))
            problems.push(problem(use.path, `het bestand "${show(use.name)}" zit niet in het pakket`));
    }
    // Credits: one per GLB and material, an allowed licence.
    const creditList = Array.isArray(json['credits']) ? json['credits'] : [];
    // A credit for every file and no more: past that nothing is walked.
    if (creditList.length > L.files)
        problems.push(problem('/credits', `hooguit ${L.files} bronvermeldingen`));
    const credits = creditList.slice(0, L.files);
    if (json['credits'] !== undefined && !Array.isArray(json['credits'])) {
        problems.push(problem('/credits', 'moet een lijst van {file, title, author, licence, source} zijn'));
    }
    const credited = new Set();
    credits.forEach((credit, i) => {
        const at = `/credits/${i}`;
        if (!isRecord(credit)) {
            problems.push(problem(at, 'moet {file, title, author, licence, source} zijn'));
            return;
        }
        keys(credit, ['file', 'title', 'author', 'licence', 'source'], at, problems);
        if (credit['file'] !== undefined && typeof credit['file'] !== 'string') {
            problems.push(problem(`${at}/file`, 'moet een bestandsnaam zijn'));
        }
        if (typeof credit['file'] === 'string') {
            if (credited.has(credit['file']))
                problems.push(problem(`${at}/file`, `"${show(credit['file'])}" staat al in credits`));
            credited.add(credit['file']);
            if (fileList && !present.has(credit['file'])) {
                problems.push(problem(`${at}/file`, `het bestand "${show(credit['file'])}" zit niet in het pakket`));
            }
        }
        if (credit['title'] !== undefined)
            text(credit['title'], `${at}/title`, L.creditText, problems);
        if (credit['author'] !== undefined)
            text(credit['author'], `${at}/author`, L.creditText, problems);
        if (credit['licence'] !== undefined &&
            !WORLD_LICENCES.includes(credit['licence'])) {
            problems.push(problem(`${at}/licence`, 'moet cc0, cc-by-4.0, cc-by-3.0 of public-domain zijn'));
        }
        const source = credit['source'];
        if (source !== null &&
            source !== undefined &&
            (typeof source !== 'string' ||
                !/^https:\/\/\S+$/.test(source) ||
                UNSAFE_TEXT.test(source) ||
                source.length > L.creditSource)) {
            problems.push(problem(`${at}/source`, `moet null zijn of een https-adres van hooguit ${L.creditSource} tekens`));
        }
    });
    if (fileList) {
        const files = fileList;
        if (files.length > L.files)
            problems.push(problem('files', `hooguit ${L.files} bestanden`));
        const total = files.reduce((sum, f) => sum + f.bytes, 0);
        if (total > L.totalBytes)
            problems.push(problem('files', `samen hooguit ${L.totalBytes / 2 ** 20} MB`));
        for (const file of files) {
            const at = `files/${file.name}`;
            if (fileKindOf(file.name) === undefined) {
                problems.push(problem(at, 'onbekend soort bestand (wel: .glb, .bin, .hdr, .jpg, .png, .ogg)'));
                continue;
            }
            if (file.bytes > L.fileBytes)
                problems.push(problem(at, `is groter dan ${L.fileBytes / 2 ** 20} MB`));
            if (!used.has(file.name))
                problems.push(problem(at, 'wordt nergens gebruikt; haal het uit het pakket'));
            if (fileKindOf(file.name) !== 'heights' && !credited.has(file.name)) {
                problems.push(problem(at, 'heeft geen bronvermelding in credits'));
            }
        }
    }
    // Features.
    const features = isRecord(json['features']) ? json['features'] : {};
    if (json['features'] !== undefined && !isRecord(json['features']))
        problems.push(problem('/features', 'moet een object zijn'));
    if (Object.keys(features).length > L.features)
        problems.push(problem('/features', `hooguit ${L.features} onderdelen`));
    // The first block of an id: a duplicate is reported above, and states are read against the first.
    const byId = new Map();
    for (const b of blocks)
        if (!byId.has(b.id))
            byId.set(b.id, b);
    // The sun or sky of a licht block that some state changes (at most one block may set them).
    const lightTouched = new Set();
    // The states of all features together: nine features of six would be 54 buttons on the headset.
    const totalStates = Object.values(features)
        .slice(0, L.features)
        .reduce((sum, raw) => sum +
        (isRecord(raw) && isRecord(raw['states'])
            ? Math.min(Object.keys(raw['states']).length, L.states)
            : 0), 0);
    if (totalStates > L.totalStates)
        problems.push(problem('/features', `hooguit ${L.totalStates} toestanden in totaal`));
    for (const name of Object.keys(features).slice(0, L.features)) {
        const raw = features[name];
        const at = `/features/${show(name)}`;
        if (!FEATURE.test(name))
            problems.push(problem(at, 'moet een naam zijn als "getij" (kleine letters, cijfers, -)'));
        if (!isRecord(raw)) {
            problems.push(problem(at, 'moet {name, initial, seconds, round, states} zijn'));
            continue;
        }
        keys(raw, FEATURE_KEYS, at, problems, ['eerder', 'onderwerp', 'briefWoorden', 'uiterlijk']);
        if (raw['name'] !== undefined)
            text(raw['name'], `${at}/name`, 40, problems);
        // The world's own subject (ADR-069, amended 20 Oct 2026): optional, any format.
        if (raw['onderwerp'] !== undefined && typeof raw['onderwerp'] !== 'boolean')
            problems.push(problem(`${at}/onderwerp`, 'moet true of false zijn'));
        problems.push(...checkValue({ kind: 'number', min: 0, max: 30, about: '' }, raw['seconds'], `${at}/seconds`));
        const round = raw['round'];
        if (!isRecord(round))
            problems.push(problem(`${at}/round`, 'moet {centre: [x, y, z], radius, height} zijn'));
        else {
            keys(round, ['centre', 'radius', 'height'], `${at}/round`, problems);
            const c = round['centre'];
            if (!Array.isArray(c) ||
                c.length !== 3 ||
                !c.every((v) => typeof v === 'number' && Math.abs(v) <= L.extent)) {
                problems.push(problem(`${at}/round/centre`, 'moet een punt [x, y, z] zijn'));
            }
            problems.push(...checkValue({ kind: 'number', min: 0.05, max: 500, about: '' }, round['radius'], `${at}/round/radius`));
            problems.push(...checkValue({ kind: 'number', min: 0.05, max: 500, about: '' }, round['height'], `${at}/round/height`));
        }
        const states = isRecord(raw['states']) ? raw['states'] : {};
        const allNames = Object.keys(states);
        const names = allNames.slice(0, L.states);
        if (allNames.length < 1 || allNames.length > L.states)
            problems.push(problem(`${at}/states`, `moet 1 tot ${L.states} toestanden hebben`));
        if (raw['initial'] !== undefined &&
            !(typeof raw['initial'] === 'string' && names.includes(raw['initial']))) {
            problems.push(problem(`${at}/initial`, `"${show(raw['initial'])}" is geen toestand van ${show(name)} (wel: ${names.map((n) => show(n)).join(', ')})`));
        }
        for (const state of names) {
            const changes = states[state];
            const where = `${at}/states/${show(state)}`;
            if (!FEATURE.test(state))
                problems.push(problem(where, 'moet een naam zijn als "vloed" (kleine letters, cijfers, -)'));
            if (!isRecord(changes)) {
                problems.push(problem(where, 'moet {"<blok>.<instelling>": waarde} zijn'));
                continue;
            }
            // Bounded before walked: a state names a handful of settings, never a million keys.
            if (Object.keys(changes).length > L.stateChanges) {
                problems.push(problem(where, `hooguit ${L.stateChanges} instellingen per toestand`));
                continue;
            }
            for (const [key, value] of Object.entries(changes)) {
                // A key is a maker's text of any length: the path echoes it truncated.
                const shown = show(key);
                const [id, setting, ...rest] = key.length > 100 ? [] : key.split('.');
                const block = id === undefined ? undefined : byId.get(id);
                if (!block || setting === undefined || rest.length > 0) {
                    problems.push(problem(`${where}/${shown}`, `er is geen blok "${show(id ?? key)}"`));
                    continue;
                }
                if (block.type === 'licht' && (setting === 'sun' || setting === 'sky'))
                    lightTouched.add(block.id);
                const allowed = block.type === 'deeltjes'
                    ? (STATEFUL_PARTICLES[block.kind] ?? ['level'])
                    : (STATEFUL[block.type] ?? []);
                if (!allowed.includes(setting)) {
                    const name = block.type === 'deeltjes' ? `deeltjes (${block.kind})` : block.type;
                    problems.push(problem(`${where}/${shown}`, `${name}.${show(setting)} kan niet per toestand veranderen (wel: ${allowed.join(', ') || 'niets'})`));
                    continue;
                }
                // The headset makes a glow's halo only when the block glows at the start.
                if (block.type === 'deeltjes' && setting === 'glow' && block.glow === 0) {
                    problems.push(problem(`${where}/${shown}`, `${block.id}.glow kan niet per toestand veranderen: het blok heeft geen gloed (glow 0)`));
                    continue;
                }
                problems.push(...checkValue(BLOCK_SETTINGS[block.type][setting], value, `${where}/${shown}`));
            }
        }
    }
    problems.push(...oldNameProblems(json, features), ...orderProblems(json['order'], features), ...briefWordProblems(features), ...appearanceProblems(features));
    if (version === 2)
        problems.push(...lightRules(blocks, lightTouched, (b) => rawIndex.get(b) ?? 0));
    return problems.length === 0
        ? { package: canonical(json, blocks, version), problems }
        : { problems };
}
/**
 * Old names (order.ts): `eerdereOnderdelen` maps an old feature name to a feature the package has, a
 * feature's `eerder` an old state name to a state it has. An old name is not a current one (it would
 * hide it), and points straight at a current name (never at another old one: no chains, no circles).
 */
function oldNameProblems(json, features) {
    const out = [];
    const check = (raw, at, current, what) => {
        if (raw === undefined)
            return;
        if (!isRecord(raw)) {
            out.push(problem(at, `moet {"<oude naam>": "<${what.one}>"} zijn`));
            return;
        }
        const entries = Object.entries(raw);
        if (entries.length > L.oldNames) {
            out.push(problem(at, `hooguit ${L.oldNames} oude namen`));
            return;
        }
        for (const [old, now] of entries) {
            const where = `${at}/${show(old)}`;
            if (!FEATURE.test(old)) {
                out.push(problem(where, 'moet een naam zijn als "rustig" (kleine letters, cijfers, -)'));
            }
            else if (current.includes(old)) {
                out.push(problem(where, `"${old}" is een ${what.one} ${what.of}: een oude naam mag geen huidige zijn`));
            }
            else if (typeof now !== 'string' || !current.includes(now)) {
                const chain = typeof now === 'string' && Object.hasOwn(raw, now);
                out.push(problem(where, chain
                    ? `"${now}" is zelf een oude naam: schrijf de huidige ${what.one} (geen ketting)`
                    : `"${show(now)}" is geen ${what.one} ${what.of} (wel: ${current.join(', ')})`));
            }
        }
    };
    check(json['eerdereOnderdelen'], '/eerdereOnderdelen', Object.keys(features), {
        one: 'onderdeel',
        of: 'van de wereld',
    });
    for (const [name, raw] of Object.entries(features).slice(0, L.features)) {
        if (!isRecord(raw))
            continue;
        const states = isRecord(raw['states']) ? Object.keys(raw['states']) : [];
        check(raw['eerder'], `/features/${show(name)}/eerder`, states, {
            one: 'toestand',
            of: `van ${show(name)}`,
        });
    }
    return out;
}
/** The most words a feature's `briefWoorden` list may hold, and the longest word (ADR-090). */
const BRIEF_WORDS_MAX = 12;
const BRIEF_WORD_LENGTH = 40;
/**
 * `briefWoorden` (ADR-090, optional, any format): `{ "onderdeel": [...], "standen": { "<toestand>": [...] } }`,
 * each list at most BRIEF_WORDS_MAX words or short phrases of at most BRIEF_WORD_LENGTH characters, and
 * `standen` naming only the feature's own states.
 */
function briefWordProblems(features) {
    const out = [];
    const list = (raw, at) => {
        if (!Array.isArray(raw) ||
            raw.length === 0 ||
            raw.length > BRIEF_WORDS_MAX ||
            !raw.every((word) => typeof word === 'string' && word.trim() !== '' && word.length <= BRIEF_WORD_LENGTH)) {
            out.push(problem(at, `moet een lijst van 1 tot ${BRIEF_WORDS_MAX} woorden zijn, elk hooguit ${BRIEF_WORD_LENGTH} tekens`));
        }
    };
    for (const [name, raw] of Object.entries(features).slice(0, L.features)) {
        if (!isRecord(raw) || raw['briefWoorden'] === undefined)
            continue;
        const at = `/features/${show(name)}/briefWoorden`;
        const words = raw['briefWoorden'];
        if (!isRecord(words)) {
            out.push(problem(at, 'moet {"onderdeel": [...], "standen": {"<toestand>": [...]}} zijn'));
            continue;
        }
        keys(words, [], at, out, ['onderdeel', 'standen']);
        if (words['onderdeel'] !== undefined)
            list(words['onderdeel'], `${at}/onderdeel`);
        const standen = words['standen'];
        if (standen === undefined)
            continue;
        if (!isRecord(standen)) {
            out.push(problem(`${at}/standen`, 'moet {"<toestand>": [...]} zijn'));
            continue;
        }
        const states = isRecord(raw['states']) ? Object.keys(raw['states']) : [];
        for (const [state, value] of Object.entries(standen)) {
            if (!states.includes(state)) {
                out.push(problem(`${at}/standen/${show(state)}`, `"${show(state)}" is geen toestand van ${show(name)} (wel: ${states.join(', ')})`));
                continue;
            }
            list(value, `${at}/standen/${show(state)}`);
        }
    }
    return out;
}
/** The longest `uiterlijk` text, and the most sentences it may have (ADR-091). */
const APPEARANCE_LENGTH = 160;
const APPEARANCE_SENTENCES = 2;
/**
 * `uiterlijk` (ADR-091, optional, any format): `{ "<toestand>": "<what a pupil sees>" }` on a feature that is the
 * world's own subject (`onderwerp: true`): only the feature's own states, each one or two sentences of at most
 * APPEARANCE_LENGTH characters, no control or direction characters.
 */
function appearanceProblems(features) {
    const out = [];
    for (const [name, raw] of Object.entries(features).slice(0, L.features)) {
        if (!isRecord(raw) || raw['uiterlijk'] === undefined)
            continue;
        const at = `/features/${show(name)}/uiterlijk`;
        const looks = raw['uiterlijk'];
        if (!isRecord(looks)) {
            out.push(problem(at, 'moet {"<toestand>": "<wat de leerling ziet>"} zijn'));
            continue;
        }
        if (raw['onderwerp'] !== true) {
            out.push(problem(at, 'hoort alleen bij een onderdeel met "onderwerp": true'));
        }
        const states = isRecord(raw['states']) ? Object.keys(raw['states']) : [];
        for (const [state, value] of Object.entries(looks)) {
            const path = `${at}/${show(state)}`;
            if (!states.includes(state)) {
                out.push(problem(path, `"${show(state)}" is geen toestand van ${show(name)} (wel: ${states.join(', ')})`));
                continue;
            }
            if (typeof value !== 'string' ||
                value.trim() === '' ||
                value.length > APPEARANCE_LENGTH ||
                sentencesOf(value).length > APPEARANCE_SENTENCES) {
                out.push(problem(path, `moet een of twee zinnen zijn van samen hooguit ${APPEARANCE_LENGTH} tekens: wat de leerling ziet`));
            }
            else if (UNSAFE_TEXT.test(value)) {
                out.push(problem(path, 'mag geen stuurtekens of tekstrichting-tekens bevatten'));
            }
        }
    }
    return out;
}
/** `order`, which the platform records at upload: every feature and every state once, nothing else. */
function orderProblems(raw, features) {
    if (raw === undefined)
        return [];
    const bad = [
        problem('/order', 'klopt niet met de onderdelen en toestanden (het platform zet het zelf)'),
    ];
    const same = (list, names) => Array.isArray(list) &&
        list.length === names.length &&
        list.every((name) => typeof name === 'string' && names.includes(name)) &&
        new Set(list).size === list.length;
    if (!isRecord(raw) || !isRecord(raw['states']))
        return bad;
    const names = Object.keys(features);
    if (Object.keys(raw).some((key) => key !== 'features' && key !== 'states'))
        return bad;
    if (!same(raw['features'], names))
        return bad;
    const states = raw['states'];
    if (!same(Object.keys(states), names))
        return bad;
    for (const name of names) {
        const feature = features[name];
        const own = isRecord(feature) && isRecord(feature['states']) ? Object.keys(feature['states']) : [];
        if (!same(states[name], own))
            return bad;
    }
    return [];
}
const copy = (value) => JSON.parse(JSON.stringify(value));
const only = (source, names) => Object.fromEntries(names.map((name) => [name, copy(source[name])]));
/**
 * The package as the checker understood it: built from the keys the format knows, copied, so nothing of
 * the caller's object (an alias, an extra key, an inherited one) is kept. Only called for a valid one.
 */
function canonical(json, blocks, version) {
    const rec = (value) => value;
    const sun = rec(json['sun']);
    const terrain = rec(json['terrain']);
    const features = Object.fromEntries(Object.entries(rec(json['features'])).map(([name, feature]) => [
        name,
        {
            ...only(rec(feature), FEATURE_KEYS),
            ...(rec(feature)['eerder'] === undefined ? {} : { eerder: copy(rec(feature)['eerder']) }),
            ...(rec(feature)['onderwerp'] === undefined
                ? {}
                : { onderwerp: rec(feature)['onderwerp'] }),
            ...(rec(feature)['briefWoorden'] === undefined
                ? {}
                : { briefWoorden: copy(rec(feature)['briefWoorden']) }),
            ...(rec(feature)['uiterlijk'] === undefined
                ? {}
                : { uiterlijk: copy(rec(feature)['uiterlijk']) }),
        },
    ]));
    const credits = json['credits'].map((credit) => only(rec(credit), [
        'file',
        'title',
        'author',
        'licence',
        'source',
    ]));
    return {
        formatVersion: json['formatVersion'],
        slug: json['slug'],
        name: only(rec(json['name']), ['nl', 'en']),
        description: json['description'],
        sky: only(rec(json['sky']), Object.keys(TOP['sky'])),
        sun: { ...only(sun, ['intensity', 'colour']), from: copy(sun['from']) },
        fog: copy(json['fog']),
        glow: json['glow'],
        background: json['background'],
        terrain: {
            glb: copy(terrain['glb']),
            heights: only(rec(terrain['heights']), ['file', 'x', 'z', 'columns', 'rows']),
        },
        blocks: copy(blocks),
        features,
        sound: only(rec(json['sound']), Object.hasOwn(rec(json['sound']), 'file') ? ['file', 'volume'] : ['soundscape', 'volume']),
        credits,
        ...(json['eerdereOnderdelen'] === undefined
            ? {}
            : { eerdereOnderdelen: copy(json['eerdereOnderdelen']) }),
        ...(json['order'] === undefined ? {} : { order: copy(json['order']) }),
        ...(version === 2
            ? {
                descriptionEn: json['descriptionEn'],
                staged: json['staged'],
                picture: json['picture'],
                props: copy(json['props']),
                ...(json['houdVrij'] === undefined ? {} : { houdVrij: copy(json['houdVrij']) }),
            }
            : {}),
    };
}
/** The files list as given, checked on its own: names unique, bytes whole numbers; the valid entries. */
function filesOf(files, problems) {
    if (files === undefined)
        return undefined;
    if (!Array.isArray(files)) {
        problems.push(problem('files', 'moet een lijst van {name, bytes} zijn'));
        return undefined;
    }
    const out = [];
    const seen = new Set();
    for (const entry of files) {
        if (!isRecord(entry) || typeof entry['name'] !== 'string') {
            problems.push(problem('files', 'elk bestand moet {name, bytes} zijn'));
            continue;
        }
        const name = entry['name'];
        const bytes = entry['bytes'];
        if (typeof bytes !== 'number' || !Number.isSafeInteger(bytes) || bytes < 0) {
            problems.push(problem(`files/${show(name)}`, 'moet een aantal bytes van 0 of meer hebben'));
            continue;
        }
        if (seen.has(name)) {
            problems.push(problem(`files/${show(name)}`, 'staat twee keer in het pakket'));
            continue;
        }
        seen.add(name);
        out.push({ name, bytes });
    }
    return out;
}
//# sourceMappingURL=validate.js.map